import { Box, Dialog, Typography } from "@mui/material";
import styles from "./priestSchedule.module.scss";
import CloseIcon from "@mui/icons-material/Close";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../common/StyledComponents/StyledFields";
import { useEffect, useState } from "react";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import CustomSelectField from "../../common/CustomSelectField/CustomSelectField";
import HcccTimePicker from "../../common/customTimePicker/hcccTimePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import {
  apiRequest,
  catchErrorMessageHandler,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import toast from "react-hot-toast";
import ErrorPopup from "../../common/ErrorPopup";
import constants from "../../../config/constants.json";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import { object, string } from "yup";
import { checkOverlapTimings } from "../../common/handleTimeChangeSetErrors";
import SchedulingBar from "./SchedulingScreen/SchedulingBar";

const priestDetailsStyles = {
  poojaBookingFieldContainer: {
    maxWidth: { xs: "100%", sm: "320px" },
    width: { xs: "100%", sm: "320px" },
    pr: { xs: "0", sm: "15px" },
    mb: "10px",
  },
  poojaBookingGroupedContainer: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
  },
};

interface PriestScheduleDialogProps {
  open: boolean;
  handleClose: () => void;
  schedulePriestData: any;
  weeklySchedules: any;
  isModifyOrDelete: boolean;
  handleDeletePriestSchedule: (id: any) => void;
  handleSchedulePriest: (data: any) => void;
  handleUpdateSchedulePriest: (data: any) => void;
}

const PriestScheduleDialog = (props: PriestScheduleDialogProps) => {
  const {
    handleClose,
    open,
    schedulePriestData,
    isModifyOrDelete,
    handleDeletePriestSchedule,
    handleSchedulePriest,
    handleUpdateSchedulePriest,
    weeklySchedules,
  } = props;

  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [poojaItemsList, setPoojaItemsList] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isDeletedBtnLoading, setIsDeleteBtnLoading] = useState(false);
  const [isUpdateBtnLoading, setIsUpdateBtnLoading] = useState(false);
  const [timeErrorMessage, setTimeErrorMessage] = useState<any>(null);

  const poojaLocations = [
    { value: "TEMPLE_POOJA", label: "Temple Pooja" },
    { value: "OUTSIDE_POOJA", label: "Outside Pooja" },
    { value: "PRIEST_DUTIES", label: "Priest Duties" },
  ];

  const initialValues = {
    priestId: "",
    date: "",
    poojaLocation: "",
    poojaId: "",
    startTime: "",
    endTime: "",
    description: "",
    priestName: "",
  };

  const validationSchema = object().shape({
    priestId: string().required("Priest name is required"),
    date: string().required("Date is required"),
    poojaLocation: string().required("Pooja location is required"),
    poojaId: string().required("Pooja is required"),
    description: string().required("Description is required"),
    priestName: string().required("Priest name is required"),
    startTime: string().required("Start time is required"),
    endTime: string().required("End time is required"),
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit(values) {
      if (!formik.isValid) {
        return;
      } else {
        let schedules = getSchedulesByDayAndPriest();
        schedules =
          schedules?.schedules?.filter(
            (each: any) => each?.scheduleId !== schedulePriestData?.scheduleId
          ) || [];
        const errorMessage = checkOverlapTimings(
          values.startTime,
          values.endTime,
          schedules
        );
        if (!errorMessage) {
          if (isModifyOrDelete) {
            handleUpdateSchedulePriest({
              ...values,
              scheduleId: schedulePriestData?.scheduleId,
              devoteeId: schedulePriestData?.devoteeId
            });
            setIsUpdateBtnLoading(true);
          } else {
            handleSchedulePriest(values);
            setIsUpdateBtnLoading(true);
          }
        }
        setTimeErrorMessage(errorMessage);
      }
    },
    enableReinitialize: true,
  });

  const getSchedulesByDayAndPriest = () => {
    let schedules = weeklySchedules?.find(
      (data: any) =>
        data?.date?.date === schedulePriestData?.date &&
        data?.priestOrHallId === schedulePriestData?.priestId
    );
    return schedules;
  };

  useEffect(() => {
    if (formik.values.poojaLocation) {
      apiRequest({
        method: "GET",
        endpoint: apiPath.poojaTypeByPoojaLocation,
        params: {
          poojaType: formik.values.poojaLocation,
        },
        includeToken: true,
      })
        .then((res) => {
          setPoojaItemsList(res.data);
        })
        .catch((err) => {
          console.log(err);
          // eslint-disable-next-line react/jsx-no-undef
          toast.custom(
            <ErrorPopup
              showMessage={catchErrorMessageHandler(err)}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />,
            {
              duration: constants.TOAST_POPUP_DURATION,
            }
          );
        });
    }
  }, [formik.values.poojaLocation]);

  useEffect(() => {
    if (schedulePriestData) {
      formik.setFieldValue("priestId", schedulePriestData?.priestId);
      formik.setFieldValue("date", schedulePriestData?.date);
      formik.setFieldValue("poojaLocation", schedulePriestData?.poojaLocation);
      formik.setFieldValue("poojaId", schedulePriestData?.poojaId);
      formik.setFieldValue("startTime", schedulePriestData?.startTime);
      formik.setFieldValue("endTime", schedulePriestData?.endTime);
      formik.setFieldValue("description", schedulePriestData?.description);
      formik.setFieldValue("priestName", schedulePriestData?.priestName);
    }
  }, [schedulePriestData]);

  const handleDateChange = (field: string, value: any) => {
    const formattedDate = dayjs(value, "MM-DD-YYYY");
    const date = formattedDate.isValid()
      ? formattedDate.format("MM-DD-YYYY")
      : "";
    formik.setFieldValue(field, date);
  };

  const handleFieldChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const handleFieldsTouched = () => {
    formik.setTouched(
      {
        priestName: true,
        date: true,
        poojaLocation: true,
        poojaId: true,
        description: true,
        startTime: true,
        endTime: true,
      },
      true
    ); // This forces validation errors to show
    formik.setFieldTouched("description", true);
  };

  const renderFieldsComponent = () => {
    return (
      <>
        <Box sx={priestDetailsStyles.poojaBookingGroupedContainer}>
          <Box sx={priestDetailsStyles.poojaBookingFieldContainer}>
            {CustomRequiredInputLabel("Priest Name", true)}
            <CustomTextFeild
              fullWidth
              required
              value={formik.values.priestName}
              className={styles.textField}
              placeholder="Priest name"
              InputProps={{
                inputProps: {
                  readOnly: true,
                },
              }}
              disabled
            />
          </Box>
          <Box sx={priestDetailsStyles.poojaBookingFieldContainer}>
            {CustomRequiredInputLabel("Date", true)}
            <CustomDatePickerField
              isDatePickerOpen={isDatePickerOpen}
              setDatePickerOpen={setDatePickerOpen}
              date={formik.values.date}
              handleDateChange={handleDateChange}
              errorMessage={formik.touched.date ? formik.errors.date : ""}
              readOnly={true}
            />
          </Box>
        </Box>
        <Box sx={priestDetailsStyles.poojaBookingGroupedContainer}>
          <Box sx={priestDetailsStyles.poojaBookingFieldContainer}>
            {CustomRequiredInputLabel("Location/Priest Duties", true)}
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={
                formik.touched.poojaLocation ? formik.errors.poojaLocation : ""
              }
              dropDownList={poojaLocations}
              value={formik.values.poojaLocation}
              fieldName="poojaLocation"
              placeholder={"Pooja location"}
              optionLabel={"label"}
              optionValue={"value"}
              readOnly={true}
            />
          </Box>
          <Box sx={priestDetailsStyles.poojaBookingFieldContainer}>
            {CustomRequiredInputLabel("Pooja Type", true)}
            <CustomSelectField
              handleDropdownChange={handleFieldChange}
              errorMessage={formik.touched.poojaId ? formik.errors.poojaId : ""}
              dropDownList={poojaItemsList}
              value={formik.values.poojaId}
              fieldName="poojaId"
              placeholder={"Select pooja"}
              optionLabel={"name"}
              optionValue={"id"}
              readOnly={true}
            />
          </Box>
        </Box>
        <Box
          sx={{
            ...priestDetailsStyles.poojaBookingFieldContainer,
            width: { xs: "100%", sm: "calc(100% - 15px)" },
            maxWidth: "100%",
          }}
        >
          {CustomRequiredInputLabel("Description", true)}
          <textarea
            className={styles.text_area_field}
            value={formik.values.description}
            onChange={(e) => handleFieldChange("description", e.target.value)}
          />
          {formik.touched.description && formik?.errors?.description ? (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {formik?.errors?.description}
            </Typography>
          ) : (
            ""
          )}
        </Box>
        <Box sx={priestDetailsStyles.poojaBookingGroupedContainer}>
          <Box sx={priestDetailsStyles.poojaBookingFieldContainer}>
            <HcccTimePicker
              handleFieldChange={handleFieldChange}
              labelName="Select Start Time"
              fieldName="startTime"
              className={
                formik.touched.startTime && formik?.errors?.startTime
                  ? styles.errorTextField
                  : styles.textField
              }
              value={formik.values.startTime}
              isLabelNotRequired={false}
            />
            {formik.touched.startTime && formik?.errors?.startTime ? (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {formik?.errors?.startTime}
              </Typography>
            ) : (
              ""
            )}
          </Box>
          <Box sx={priestDetailsStyles.poojaBookingFieldContainer}>
            <HcccTimePicker
              handleFieldChange={handleFieldChange}
              labelName="Select End Time"
              fieldName="endTime"
              className={
                formik.touched.endTime && formik?.errors?.endTime
                  ? styles.errorTextField
                  : styles.textField
              }
              value={formik.values.endTime}
              isLabelNotRequired={false}
            />
            {formik.touched.endTime && formik?.errors?.endTime ? (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {formik?.errors?.endTime}
              </Typography>
            ) : (
              ""
            )}
          </Box>
        </Box>
        {timeErrorMessage ? (
          <Typography
            sx={{
              color: "red",
              fontSize: "13px",
              fontWeight: "bold",
              mb: "15px",
              mt: "5px",
            }}
          >
            {timeErrorMessage}
          </Typography>
        ) : (
          ""
        )}
      </>
    );
  };

  const buttonsComponent = () => {
    return (
      <Box className={styles.buttonsContainer}>
        {isModifyOrDelete ? (
          <>
            <CommonButton
              onClick={() => {
                handleFieldsTouched();
                if (formik.isValid && !isUpdateBtnLoading) {
                  formik.handleSubmit();
                }
              }}
              sx={{ mr: "10px" }}
            >
              <ButtonLoader
                isBtnLoading={isUpdateBtnLoading}
                btnText={"Update"}
              />
            </CommonButton>
            <CommonOutlinedButton
              onClick={() => {
                if (!isDeletedBtnLoading) {
                  handleDeletePriestSchedule(schedulePriestData?.scheduleId);
                  setIsDeleteBtnLoading(true);
                }
              }}
              sx={{ mr: "10px" }}
            >
              <ButtonLoader
                isBtnLoading={isDeletedBtnLoading}
                btnText={"Delete"}
              />
            </CommonOutlinedButton>
          </>
        ) : (
          <CommonButton
            onClick={() => {
              handleFieldsTouched();
              if (formik.isValid && !isUpdateBtnLoading) {
                formik.handleSubmit();
              }
            }}
            sx={{ mr: "10px" }}
          >
            <ButtonLoader
              isBtnLoading={isUpdateBtnLoading}
              btnText={"Submit"}
            />
          </CommonButton>
        )}
        <CommonOutlinedButton onClick={handleClose}>
          Cancel
        </CommonOutlinedButton>
      </Box>
    );
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: "710px" },
          maxWidth: { xs: "100%", sm: "710px" },
          margin: { xs: "10px", sm: "20px" },
        },
      }}
    >
      <Box
        className={styles.schedule_dialog_main_container}
        sx={{
          width: "100%",
        }}
      >
        <Box className={styles.dialog_title_container}>
          <Typography className={styles.title}>
            {isModifyOrDelete
              ? "Modify / Delete Priest Schedule"
              : "Add Priest Schedule (One Time Schedule)"}
          </Typography>
          <CloseIcon
            sx={{ color: "white", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Box>
        <Box className={styles.dialog_inner_container}>
          {renderFieldsComponent()}
          <Box sx={{ pr: { xs: "0px", sm: "14px" }, my: "15px" }}>
            <SchedulingBar
              handleClickAdd={() => {}}
              priestSchedulesData={getSchedulesByDayAndPriest()}
              isOnlyBar={true}
            />
          </Box>
          {buttonsComponent()}
        </Box>
      </Box>
    </Dialog>
  );
};

export default PriestScheduleDialog;
